<template>
  <aside
    v-if="isVisible"
    class="w-full lg:w-[480px] bg-card rounded-2xl shadow-sm border border-border flex flex-col overflow-hidden animate-in slide-in-from-right-10 duration-500 h-full lg:h-auto"
  >
    <!-- Sidebar Header -->
    <div
      class="h-20 border-b border-border flex items-center justify-between px-8 shrink-0"
    >
      <h2 class="font-bold text-[20px] text-foreground">Modules and Lessons</h2>
      <button
        @click="$emit('close')"
        class="p-2 hover:bg-muted/50 rounded-lg transition-colors"
      >
        <X class="w-6 h-6 text-muted-foreground" />
      </button>
    </div>

    <!-- Content -->
    <div class="overflow-y-auto p-4 lg:p-8 space-y-6 lg:space-y-10">
      <div v-for="(module, mIndex) in modules" :key="module.id || mIndex">
        <!-- Module Header -->
        <div
          class="flex items-center justify-between gap-1 group cursor-pointer mb-6"
          @click="toggleModule(mIndex)"
        >
          <span class="text-[16px] text-muted-foreground uppercase">
            {{ module.name || `Module ${mIndex + 1}` }}
          </span>
          <ChevronUp
            class="w-4 h-4 text-muted-foreground transition-transform duration-200"
            :class="{ 'rotate-180': !expandedModules[mIndex] }"
          />
        </div>

        <!-- Lessons List -->
        <div class="space-y-3" v-show="expandedModules[mIndex]">
          <div
            v-for="(topic, tIndex) in module.topics"
            :key="topic.name || tIndex"
            class="flex items-start gap-5 p-4 rounded-xl transition-all duration-200 cursor-pointer border border-transparent"
            :class="[
              selectedTopic?.name === topic.name
                ? 'bg-accent/80 border-border'
                : 'hover:bg-muted/30',
              isTopicLocked(mIndex, tIndex) ? 'opacity-50 grayscale' : '',
            ]"
            @click="handleTopicClick(topic, mIndex, tIndex)"
          >
            <!-- Checkbox -->
            <div
              class="w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all duration-300 cursor-pointer hover:border-primary"
              :class="[
                isTopicCompleted(topic)
                  ? 'bg-primary border-primary text-primary-foreground'
                  : 'border-border bg-background',
              ]"
              @click.stop="handleTopicClick(topic, mIndex, tIndex, true)"
            >
              <Check
                v-if="isTopicCompleted(topic)"
                class="w-3.5 h-3.5 stroke-[3]"
              />
            </div>

            <!-- Text Content -->
            <div class="flex-1">
              <p
                class="text-[16px] font-semibold text-foreground leading-tight mb-2"
                :class="{
                  'text-muted-foreground line-through': isTopicCompleted(topic),
                }"
              >
                {{ tIndex + 1 }}. {{ topic.name }}
              </p>

              <!-- Meta Icons -->
              <div class="flex items-center flex-wrap gap-x-6 gap-y-2">
                <template v-if="topic.resources">
                  <!-- Videos -->
                  <div
                    v-for="(video, vIndex) in topic.resources.videos || []"
                    :key="video.video_id || vIndex"
                    class="flex items-center gap-2 cursor-pointer transition-colors"
                    :class="[
                      isVideoActive(topic, video.video_url)
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground',
                    ]"
                    @click.stop="handleVideoClick(video, mIndex, tIndex)"
                  >
                    <Youtube
                      class="w-4 h-4"
                      :class="[
                        isVideoActive(topic, video.video_url)
                          ? 'text-primary'
                          : 'text-foreground',
                      ]"
                    />
                    <span class="text-[14px] font-bold">
                      {{
                        isVideoActive(topic, video.video_url)
                          ? "Watching"
                          : "Watch"
                      }}
                      {{ vIndex + 1 }}
                    </span>
                  </div>

                  <!-- Internal Resources (AI Content) -->
                  <div
                    v-for="(res, rIndex) in topic.resources.internal_resources || []"
                    :key="'internal-' + rIndex"
                    class="flex items-center gap-2 cursor-pointer transition-colors"
                    :class="[
                      isTextActive(topic, res.content)
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground',
                    ]"
                    @click.stop="handleTextClick(res, mIndex, tIndex)"
                  >
                    <FileText
                      class="w-4 h-4"
                      :class="[
                        isTextActive(topic, res.content)
                          ? 'text-primary'
                          : 'text-foreground',
                      ]"
                    />
                    <span class="text-[14px] font-bold">
                      Read
                    </span>
                  </div>

                  <!-- Academic Papers -->
                  <div
                    v-for="(paper, pIndex) in topic.resources.papers || []"
                    :key="'paper-' + pIndex"
                    class="flex items-center gap-2 cursor-pointer transition-colors"
                    :class="[
                      isPaperActive(topic, pIndex)
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground',
                    ]"
                    @click.stop="handlePaperClick(pIndex, mIndex, tIndex)"
                  >
                    <BookOpen
                      class="w-4 h-4"
                      :class="[
                        isPaperActive(topic, pIndex)
                          ? 'text-primary'
                          : 'text-foreground',
                      ]"
                    />
                    <span class="text-[14px] font-bold uppercase tracking-tight">
                      Paper {{ pIndex + 1 }}
                    </span>
                  </div>

                  <!-- Practice Task -->
                  <div
                    v-if="topic.practice_task"
                    class="flex items-center gap-2 cursor-pointer transition-colors"
                    :class="[
                      isPracticeTaskActive(topic)
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground',
                    ]"
                    @click.stop="handlePracticeTaskClick(mIndex, tIndex)"
                  >
                    <BookCheck
                      class="w-4 h-4"
                      :class="[
                        isPracticeTaskActive(topic)
                          ? 'text-primary'
                          : 'text-foreground',
                      ]"
                    />
                    <span class="text-[14px] font-bold">
                      Practice Task
                    </span>
                  </div>
                </template>
                <div
                  v-else
                  class="flex items-center gap-2 text-muted-foreground"
                >
                  <Sparkles class="w-4 h-4 text-foreground" />
                  <span class="text-[12px] font-bold">Interactive</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import {
  X,
  Check,
  ChevronUp,
  Youtube,
  FileText,
  BookOpen,
  Sparkles,
  BookCheck,
} from "lucide-vue-next";

export default {
  name: "ModulesSidebar",
  components: {
    X,
    Check,
    ChevronUp,
    Youtube,
    FileText,
    BookOpen,
    Sparkles,
    BookCheck,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    modules: {
      type: Array,
      default: () => [],
    },
    selectedTopic: {
      type: Object,
      default: () => null,
    },
    currentVideoUrl: {
      type: String,
      default: null,
    },
    activeTopicId: {
      type: [String, Number],
      default: null,
    },
  },
  emits: ["close", "select-topic", "play-video", "play-text", "play-paper", "play-practice-task", "toggle-completion"],
  data() {
    return {
      expandedModules: {},
    };
  },
  watch: {
    modules: {
      immediate: true,
      handler(newModules) {
        // Initialize all modules as expanded by default
        if (newModules) {
          newModules.forEach((_, index) => {
            if (this.expandedModules[index] === undefined) {
              this.expandedModules[index] = true;
            }
          });
        }
      },
    },
  },
  methods: {
    getEmbedUrl(url) {
      if (!url) return null;
      let videoId = null;
      if (url.includes("youtube.com/watch?v=")) {
        videoId = url.split("v=")[1].split("&")[0];
      } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1];
      }
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
      }
      return url;
    },
    isVideoActive(topic, videoUrl) {
      if (!this.currentVideoUrl || !videoUrl || !this.activeTopicId)
        return false;
      const topicId = topic.task_id || topic.id;
      return (
        topicId === this.activeTopicId &&
        this.currentVideoUrl === this.getEmbedUrl(videoUrl)
      );
    },
    isTextActive(topic, content) {
      if (this.currentVideoUrl || !this.activeTopicId || !topic.lesson_body || this.$parent.currentPaperIndex !== null)
        return false;
      const topicId = topic.task_id || topic.id;
      return topicId === this.activeTopicId && topic.lesson_body === content;
    },
    isPaperActive(topic, paperIndex) {
      if (this.activeTopicId === null || this.$parent.currentPaperIndex === null) return false;
      const topicId = topic.task_id || topic.id;
      return topicId === this.activeTopicId && paperIndex === this.$parent.currentPaperIndex;
    },
    isPracticeTaskActive(topic) {
      if (this.activeTopicId === null || !this.$parent.showPracticeTask) return false;
      const topicId = topic.task_id || topic.id;
      return topicId === this.activeTopicId;
    },
    toggleModule(index) {
      this.expandedModules[index] = !this.expandedModules[index];
    },
    isTopicLocked(mIndex, tIndex) {
      // First topic of first module is always unlocked
      if (mIndex === 0 && tIndex === 0) return false;

      // Check previous topic in the same module
      if (tIndex > 0) {
        const prevTopic = this.modules[mIndex].topics[tIndex - 1];
        return !this.isTopicCompleted(prevTopic);
      }

      // Check the last topic of the previous module
      if (mIndex > 0) {
        const prevModule = this.modules[mIndex - 1];
        if (prevModule.topics && prevModule.topics.length > 0) {
          const lastTopicOfPrev =
            prevModule.topics[prevModule.topics.length - 1];
          return !this.isTopicCompleted(lastTopicOfPrev);
        }
        // If previous module has no topics, theoretically we should check the one before that,
        // but let's assume valid data structure for now.
        return false;
      }
      return false;
    },
    isTopicCompleted(topic) {
      return (
        topic.status === "Completed" ||
        topic.completed === true ||
        (topic.completed && topic.completed !== "false")
      );
    },
    handleVideoClick(video, mIndex, tIndex) {
      if (this.isTopicLocked(mIndex, tIndex)) {
        this.$toast?.error(
          "Locked",
          "Please complete the previous task first.",
        );
        return;
      }
      // Emit event to parent to play video in main area
      const url = video.video_url;
      if (url) {
        const topic = this.modules[mIndex].topics[tIndex];
        this.$emit("play-video", { url, topicId: topic.task_id || topic.id });
      }
    },
    handleTextClick(resource, mIndex, tIndex) {
      if (this.isTopicLocked(mIndex, tIndex)) {
        this.$toast?.error(
          "Locked",
          "Please complete the previous task first.",
        );
        return;
      }
      const topic = this.modules[mIndex].topics[tIndex];
      // Ensure the topic has the lesson_body set to this content
      topic.lesson_body = resource.content;
      this.$emit("play-text", { content: resource.content, topicId: topic.task_id || topic.id });
    },
    handlePaperClick(paperIndex, mIndex, tIndex) {
      if (this.isTopicLocked(mIndex, tIndex)) {
        this.$toast?.error(
          "Locked",
          "Please complete the previous task first.",
        );
        return;
      }
      const topic = this.modules[mIndex].topics[tIndex];
      this.$emit("play-paper", { paperIndex, topicId: topic.task_id || topic.id });
    },
    handlePracticeTaskClick(mIndex, tIndex) {
      if (this.isTopicLocked(mIndex, tIndex)) {
        this.$toast?.error(
          "Locked",
          "Please complete the previous task first.",
        );
        return;
      }
      const topic = this.modules[mIndex].topics[tIndex];
      this.$emit("play-practice-task", { topicId: topic.task_id || topic.id });
    },
    handleTopicClick(topic, mIndex, tIndex, isCheckbox = false) {
      if (this.isTopicLocked(mIndex, tIndex)) {
        this.$toast?.error(
          "Locked",
          "Please complete the previous task first.",
        );
        return;
      }
      if (isCheckbox) {
        this.$emit("toggle-completion", topic);
      } else {
        this.$emit("select-topic", topic);
      }
    },
    handleToggleCompletion(topic) {
      // Deprecated direct usage, routed through handleTopicClick for lock check
      this.$emit("toggle-completion", topic);
    },
  },
};
</script>

<style scoped>
/* Scoped styles if needed */
</style>
